import { PlanType } from '@labrav/react-components'
import { Reducer } from 'redux'
import { FreeTrialStatus } from '../freeTrials/reducer'
import { Features } from '../privateOffer/reducer'
import {
  AzurePriceInputOption,
  AzureRecurrentPriceField,
  EulaType,
  IncludedQuantity,
  PlanTypeGCP,
  Price,
} from '../privateOffer/reducer'
import { ProductsListingActionTypes } from './actions'
import { FlyoutMicrosoftProductSections } from '../addEditProduct/reducer'

export interface links {
  self: string
  nextPage?: string
  previousPage?: string
}

export type CloudMarketplace = 'AWS' | 'GCP' | 'AZURE' | 'REDHAT'

export enum FlyOutAWSProductListingType {
  'SAAS' = 'SAAS',
  'PROFESSIONAL_SERVICES' = 'PROFESSIONAL_SERVICES',
  'CONTAINER' = 'CONTAINER',
  'AGENTIC_AI' = 'AGENTIC_AI',
}

export interface FreeTrial {
  freeTrialStatus?: FreeTrialStatus
  freeTrialEnabled?: boolean
  eulaType?: EulaType
  eulaFiles?: string[]
  freeTrial?: boolean
  freeTrialEnabledAt?: string
  labraFreeTrialId?: string
  freeTrialOfferId?: string
  freeTrialExpiresInDays?: number
  cloudFreeTrialOfferId?: string
  cloudFreeTrialOfferName?: string
  enabled?: boolean
  quantity?: number
}

export enum ProductStatus {
  PRODUCT_STATUS_PUBLIC = 'Public',
  PRODUCT_STATUS_IN_PROGRESS = 'In-progress',
  PRODUCT_STATUS_LABRA_TESTING = 'Labra-testing',
  PRODUCT_STATUS_LIMITED = 'Limited',
  PRODUCT_STATUS_DRAFT = 'Draft',
  PRODUCT_STATUS_CREATION_FAILED = 'Failed',
  PRODUCT_STATUS_AWS_TESTING = 'AWS-testing',
  PRODUCT_STATUS_RESTRICTED = 'Restricted',
  PRODUCT_STATUS_REJECTED = 'Rejected',
  PRODUCT_STATUS_MIGRATION_IN_QUEUE = 'MIGRATION_IN_QUEUE',
  MIGRATION_FAILED = 'MIGRATION_FAILED',
  MIGRATION_IN_PROGRESS = 'MIGRATION_IN_PROGRESS',
}
export enum ContainerPricingModel {
  CONTRACT = 'CONTRACT',
  HOURLY = 'HOURLY',
  USAGE = 'USAGE',
  FREE = 'FREE',
  BYOL = 'BYOL',
  MONTHLY = 'MONTHLY',
}

export enum PriceTypes {
  'PRICE_1M' = 'price1m',
  'PRICE_12M' = 'price12m',
  'PRICE_24M' = 'price24m',
  'PRICE_36M' = 'price36m',
  'ADDITIONAL_USAGE_PRICE' = 'additionalUsagePrice',
  'SUSCRIPTION_PRICE' = 'subscriptionPrice',
}

export const resalePricesKey: string[] = [
  'price1m',
  'price12m',
  'price24m',
  'price36m',
  'additionalUsagePrice',
  'subscriptionPrice',
]

export interface CommonDimensionField {
  dimensionId?: string
  id?: string
  label: string
  name: string
  valueDataType?: string
  price?: Price
  createdAt?: string
  currency: string
}

export interface ProductDimension extends CommonDimensionField {
  type?: string
  labelDescription: string
  isAdditionalUsageAllowed?: boolean
  additionalUsageDescription?: string
  prices?: Price
  pricePerName: string
  awsProductId: string
  freeTrial?: FreeTrial
}
export interface AzurePlanDimensionMeter {
  cloudDimensionId: string
  id?: string
  azureDimensionId: string
  label: string
  name: string
  valueDataType?: string
  createdAt?: string
  currency: string
  includedQuantities: IncludedQuantity[]
  pricePerPaymentInUsd: number
  unitOfMeasure: string
}

export interface AzurePlanDimension {
  meters: AzurePlanDimensionMeter[]
  priceInputOption: AzurePriceInputOption
}

export interface GcpPlanPrice {
  price1m: number | null
  price12m: number | null
  price24m: number | null
  price36m: number | null
}
export interface ProductPlan {
  planId: string
  azurePlanId?: string
  cloudPlanId?: string
  displayName: string
  description: string
  minQuantity?: number
  maxQuantity?: number
  planType?: PlanTypeGCP
  prices?: GcpPlanPrice
  dimensions: AzurePlanDimension | CommonDimensionField[]
  pricing?: AzureRecurrentPriceField[]
  features?: Features[]
  isPrivate?: boolean
  isStopSell?: boolean
  hasFreeTrials?: boolean
}
export interface Listingdata {
  searchKeywords?: string
  searchKeyword?: string
}

export interface Product {
  id?: string
  listingData?: Listingdata | FlyoutMicrosoftProductSections
  productId: string
  productName: string
  cloudMarketplaceProductId: string
  numberOfContracts: number
  eulaUrl?: string
  numberOfActiveContracts?: number
  cloudMarketplace: CloudMarketplace
  status: ProductStatus
  marketplaceUrl: string
  productDimensions?: ProductDimension[]
  sellerId: string
  createdAt: string
  productType?: PlanType
  updatedAt: string
  freeTrial?: FreeTrial
  links: links
  emailCustomMessage?: string
  plans?: ProductPlan[]
  containerPricingModel?: ContainerPricingModel
  awsProductId?: string
  isAgenticProduct?: boolean
}

export const notAllowedProductSelection = [
  ProductStatus.PRODUCT_STATUS_DRAFT,
  ProductStatus.PRODUCT_STATUS_IN_PROGRESS,
  ProductStatus.PRODUCT_STATUS_REJECTED,
  ProductStatus.PRODUCT_STATUS_RESTRICTED,
]

export const allowedToDeleteProductSelection = [
  ProductStatus.PRODUCT_STATUS_DRAFT,
  ProductStatus.PRODUCT_STATUS_CREATION_FAILED,
]

export const allowedToPublishProductSelection = [
  ProductStatus.PRODUCT_STATUS_REJECTED,
  ProductStatus.PRODUCT_STATUS_RESTRICTED,
]

export const allowedToUnpublishProductSelection = [
  ProductStatus.PRODUCT_STATUS_PUBLIC,
]

export interface FreeTierInfo {
  additionalAllowedProducts?: number
}

export interface ProductsListingState {
  selectedProduct?: string
  products: {
    rows: Product[]
  }
  productWithListingData?: {
    rows: Product[]
  }
  freeTierInfo?: FreeTierInfo
}

export const initialState: ProductsListingState = {
  products: {
    rows: [],
  },
}

type actionType =
  | {
      type: ProductsListingActionTypes.SET_PRODUCTS
      payload: ProductsListingState
    }
  | {
      type: ProductsListingActionTypes.CLEAR_PRODUCTS
    }
  | {
      type: ProductsListingActionTypes.UPDATE_CLOUD_FREE_TRIAL_OFFER_ID
      payload: { productId: string; cloudFreeTrialOfferId: string }
    }
  | {
      type: ProductsListingActionTypes.SET_SELECTED_PRODUCT
      payload: string
    }
  | {
      type: ProductsListingActionTypes.SET_FREE_TIER_DATA
      payload: FreeTierInfo
    }
  | {
      type: ProductsListingActionTypes.SET_PRODUCTS_WITH_LISTING_DATA
      payload: ProductsListingState
    }

export const reducer: Reducer<ProductsListingState, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case ProductsListingActionTypes.SET_PRODUCTS: {
      return {
        ...state,
        products: action.payload.products,
      }
    }
    case ProductsListingActionTypes.SET_PRODUCTS_WITH_LISTING_DATA: {
      return {
        ...state,
        productWithListingData: action.payload.products,
      }
    }
    case ProductsListingActionTypes.CLEAR_PRODUCTS: {
      return {
        ...state,
        products: {
          rows: [],
        },
      }
    }
    case ProductsListingActionTypes.UPDATE_CLOUD_FREE_TRIAL_OFFER_ID: {
      const { productId, cloudFreeTrialOfferId } = action.payload
      const rows = [
        ...state.products.rows.map(product => {
          if (product.productId === productId) {
            const freeTrial = {
              ...product.freeTrial,
              cloudFreeTrialOfferId,
            }
            return {
              ...product,
              freeTrial,
            }
          } else {
            return product
          }
        }),
      ]
      return {
        ...state,
        products: {
          rows,
        },
      }
    }
    case ProductsListingActionTypes.SET_SELECTED_PRODUCT: {
      return {
        ...state,
        selectedProduct: action.payload,
      }
    }
    case ProductsListingActionTypes.SET_FREE_TIER_DATA: {
      return {
        ...state,
        ...(action.payload?.additionalAllowedProducts !== undefined &&
          action.payload?.additionalAllowedProducts !== null && {
            freeTierInfo: action.payload,
          }),
      }
    }
    default: {
      return state
    }
  }
}
